import React, {useState} from 'react';
import {isNumeric, uuid} from '../../../../utils/utils';
import {PsfTemplateWithLinksEditorProps} from '../../../../@types/editor-props';
import SplitPane, {SashContent} from 'split-pane-react';
import PsfTemplateEditor from './PsfTemplateEditor';
import {PsfTemplateEventSequenceLinks} from '../../modals/canvas/PsfTemplateEventSequenceLinks';
import {isNotEditable, populateCategories} from '../../../../utils/psf-template-utils';
import {fetchAllResources, getResourceNameWithAscendants} from '../../../../utils/resource-utils';
import {ResourceType} from '../../../../@types/resource-type';
import Group from '../../../../@types/group';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField
} from '@mui/material';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Button from '@mui/material/Button';
import PsfTemplate, {PsfCategory, PsfTemplateItem} from '../../../../@types/psfTemplate';
import PsfTemplateService from '../../../../services/psf-template.service';
import {toast} from 'react-toastify';
import authService from '../../../../services/auth.service';
import {ResourceAction} from '../../../../@types/resource-action';
import {makeGroup} from '../../../../utils/group-utils';
import ErrorAlertComponent from '../../../ErrorAlert.component';

export default function PsfTemplateWithLinksEditor(props: PsfTemplateWithLinksEditorProps): React.ReactElement {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { psfTemplate, updateTab, isActive } = props;
    const uuidRef = React.useRef(uuid());
    const [panelSizePercent, setPanelSizePercent] = React.useState<(number | string)[]>(['75%', '25%']);
    const [name, setName] = React.useState<string>('');
    const [nameError, setNameError] = React.useState<boolean>(false);
    const [description, setDescription] = React.useState<string>('');
    const [groupId, setGroupId] = React.useState<number>(0);
    const [groups, setGroups] = React.useState<Group[]>([]);
    const [categories, setCategories] = useState<PsfCategory[]>([]);
    const [esdLinks, setEsdLinks] = useState<string[]>([]);
    const [error, setError] = useState<string|null>(null);
    const { setCurrentWorkspaceObject, workspaceObjects, setWorkspaceObjects, targetGroup, getAdditionalResource, addOrUpdateAdditionalResource, currentAction, setCurrentAction } = useWorkspaceContext();

    React.useEffect(() => {
        let existingGroups = getAdditionalResource(ResourceType.GROUPS).resources;
        if (!existingGroups) {
            fetchAllResources(ResourceType.GROUPS)?.then((grps) => {
                grps.sort((a, b) => a.name.localeCompare(b.name))
                setGroups(grps);
                setGroupId(psfTemplate?.group.id ?? targetGroup ?? ((grps && grps[0]) ? grps[0].id! : 0));
                addOrUpdateAdditionalResource({resources: grps, objectType: ResourceType.GROUPS});
            })
        } else {
            existingGroups.sort((a, b) => a.name.localeCompare(b.name))
            setGroups(existingGroups);
            setGroupId(psfTemplate?.group.id ?? targetGroup ?? ((existingGroups && existingGroups[0]) ? existingGroups[0].id! : 0));
        }
    }, [])

    React.useEffect(() => {
        if (psfTemplate) {
            setName(psfTemplate.name);
            setDescription(psfTemplate.description);
            if (psfTemplate.group) {
                setGroupId(psfTemplate.group.id!);
            } else {
                setGroupId(targetGroup ?? ((groups && groups.length > 0) ? groups[0].id! : 0));
            }
            setCategories(populateCategories(psfTemplate));
            if (psfTemplate.id) {
                PsfTemplateService.fetchLinksByPsfTemplateId(psfTemplate.id).then(
                    (links) => setEsdLinks(links.eventSequenceIds)
                ).catch((err) => {
                    toast.error('Error fetching PSF Template links ', err.message);
                });
            } else {
                setEsdLinks([] as string[]);
            }
        } else {
            setGroupId(targetGroup ?? ((groups && groups.length > 0) ? groups[0].id! : 0));
        }
    }, [psfTemplate, groups])

    React.useEffect(() => {
        if (isActive && currentAction === ResourceAction.SAVE) {
            setCurrentAction(null);
            handleSave();
        }
    }, [currentAction])

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setName(event.target.value);
        if (event.target.value.trim() === '') {
            setNameError(true);
        } else {
            setNameError(false);
        }
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setDescription(event.target.value);
    }

    const handleGroupChange = (event: any): void => {
        setGroupId(event.target.value as number);
    };

    const saveLinks = (psft: PsfTemplate): void => {
        PsfTemplateService.savePsfEventSequenceLinks({psfTemplateId: psft.id, eventSequenceIds: esdLinks.filter((esdId) => isNumeric(esdId))})
            .then(() => {
                updatePsfTemplate(psft);
                toast.success('PSF Template ' + psft.name + ' has been saved successfully.');
            })
            .catch((err) => {
                toast.error('Failed to save the links of performance shaping factors and event sequences, ', err.message);
            })
    }

    const handleSave = (): void => {
        if(validateFactors()) {
            if (psfTemplate.id) {
                PsfTemplateService.putPsfTemplate(formPsfTemplate())
                    .then((PsfTemplate: PsfTemplate) => saveLinks(PsfTemplate))
                    .catch((err) => {
                        toast.error('Failed to update the performance shaping factor template, ' + err.message);
                    });
            } else {
                PsfTemplateService.createPsfTemplate(formPsfTemplate())
                    .then((PsfTemplate: PsfTemplate) => saveLinks(PsfTemplate))
                    .catch((err) => {
                        toast.error('Failed to create the performance shaping factor template, ' + err.message);
                    });
            }
        }
    }

    const updatePsfTemplate = (psfTemplate: PsfTemplate): void => {
        let psfTemplateObject = {objectType: ResourceType.PERFORMANCE_SHAPING_FACTORS, object: psfTemplate};
        setCurrentWorkspaceObject(psfTemplateObject);
        if (workspaceObjects?.objectType === ResourceType.PERFORMANCE_SHAPING_FACTORS) {
            setWorkspaceObjects({objectType: ResourceType.PERFORMANCE_SHAPING_FACTORS});
        }
        updateTab(psfTemplateObject);
    }

    const formPsfTemplate = (): PsfTemplate => {
        const psfTemplateData = {} as PsfTemplate;
        psfTemplateData.id = psfTemplate.id;
        psfTemplateData.name = name;
        psfTemplateData.description = description;
        psfTemplateData.group = makeGroup(groups.find((group) => group.id === groupId));
        if (categories?.length > 0) {
            psfTemplateData.presentation = { v1: { orderedPsfTemplpateItemsIncludingEmptyCategories: [] }};
            psfTemplateData.psfTemplateItems = [] as PsfTemplateItem[];
            categories.forEach((category) => {
                if (category.psfTemplateItems && category.psfTemplateItems.length > 0) {
                    psfTemplateData.presentation.v1.orderedPsfTemplpateItemsIncludingEmptyCategories.push(
                        [category.path, category.psfTemplateItems!.map((item) => item.name)]);
                    psfTemplateData.psfTemplateItems.push(...category.psfTemplateItems);
                } else {
                    psfTemplateData.presentation.v1.orderedPsfTemplpateItemsIncludingEmptyCategories.push(
                        [category.path, []]);
                }
            })
        }
        return psfTemplateData;
    }

    const handleDeleteCategory = (category: PsfCategory): void => {
        setCategories(categories.filter((cat) => !cat.path.startsWith(category.path)));
    }

    const validateFactors = (): boolean => {
        let valid = true;
        if (categories?.length > 0) {
            categories.forEach((category) => {
                if (category.psfTemplateItems && category.psfTemplateItems.length > 0) {
                    if (category.psfTemplateItems.find((item) => item.name?.trim() === '')) {
                        setError('One or more performance shaping factors have empty names. Factor name is required.');
                        valid = valid && false;
                    }
                    if (category.psfTemplateItems.filter((item, index) =>
                        category.psfTemplateItems?.findIndex(
                            (factor) => factor.name === item.name ||
                                (factor.code !== '' && factor.code === item.code)) !== index).length > 0) {
                        setError('There are performance shaping factors have duplicated names or codes. Factor name and code have to be unique within the same category.');
                        valid = valid && false;
                    }
                }
            });
        }
        return valid;
    }

    if (groups && groups[0] && (groupId == 0 || isNaN(groupId))) { setGroupId(targetGroup ?? groups[0].id!); }

    return (<Box sx={{ width: '100%' }}>
        <Stack spacing={2} direction="column" justifyContent='space-evenly'>
            <TableContainer component={Paper}>
                <Table sx={{minWidth: 650}} aria-label="simple table">
                    <TableBody>
                        <TableRow key={0}>
                            <TableCell component="th" scope="row" width='33%'>
                                <TextField
                                    label='Name'
                                    aria-label='name'
                                    type='text'
                                    variant="outlined"
                                    size='small'
                                    id={`name-input-${uuidRef.current}`}
                                    autoComplete='on'
                                    value={name || ''}
                                    onChange={handleNameChange}
                                    sx={{
                                        '& input': {
                                            border: 'none',
                                        }
                                    }}
                                    disabled={isNotEditable(psfTemplate)}
                                    required
                                    fullWidth
                                    helperText={nameError ? 'Name is required' : ''}
                                    error={nameError}
                                />
                            </TableCell>
                            <TableCell width='33%'>
                                <TextField
                                    label='Description'
                                    aria-label='description'
                                    type='text'
                                    variant="outlined"
                                    size='small'
                                    id={`description-input-${uuidRef.current}`}
                                    autoComplete='on'
                                    value={description || ''}
                                    onChange={handleDescriptionChange}
                                    sx={{
                                        '& input': {
                                            border: 'none',
                                        }
                                    }}
                                    disabled={isNotEditable(psfTemplate)}
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell width='33%'>
                                <FormControl fullWidth>
                                    <InputLabel tabIndex={-1} id={`select-label-${uuidRef.current}`}
                                        htmlFor={`select-list-${uuidRef.current}`}
                                        size='small'>Working Area</InputLabel>
                                    <Select
                                        role='presentation'
                                        labelId={`select-label-${uuidRef.current}`}
                                        id={`select-${uuidRef.current}`}
                                        value={groupId}
                                        label='Working Area'
                                        size='small'
                                        fullWidth={true}
                                        onChange={handleGroupChange}
                                        disabled={psfTemplate.id !== undefined}
                                    >
                                        {psfTemplate.id ?
                                            <MenuItem key={psfTemplate.group.id} value={psfTemplate.group.id}
                                                dense>{getResourceNameWithAscendants(psfTemplate.group, ResourceType.GROUPS)}</MenuItem>
                                            :
                                            (groups.filter((grp: Group) => grp.canEdit()).map((grp: Group) =>
                                                <MenuItem key={grp.id} value={grp.id}
                                                    dense>{getResourceNameWithAscendants(grp, ResourceType.GROUPS)}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <Button
                                    variant='contained'
                                    title='Save PsfTemplate Report'
                                    component='button'
                                    onClick={handleSave}
                                    startIcon={<SaveAltIcon />}
                                    disabled={isNotEditable(psfTemplate, authService.isSuperUser()) || name === undefined || name.trim() === '' || isNaN(groupId) || groupId <= 0}
                                >Save</Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <div id={uuidRef.current} style={{
                width: '100vw',
                height: 'calc(100vh - 250px)',
                maxWidth: '100%',
                maxHeight: '100%',
                overflow: 'hidden'
            }}>
                <SplitPane
                    split="vertical"
                    sizes={panelSizePercent}
                    onChange={setPanelSizePercent}
                    sashRender={(index: number, active: boolean): React.ReactNode => (
                        <SashContent
                            className={active ? 'action-sash-wrap' : 'hidden-sash'}
                        >
                        </SashContent>
                    )}
                >
                    <PsfTemplateEditor
                        psfTemplate={psfTemplate}
                        categories={categories}
                        updateCategories={setCategories}
                        handleDeleteCategory={handleDeleteCategory}
                        isActive={isActive}
                        updateTab={updateTab}/>
                    <PsfTemplateEventSequenceLinks title='Linked Safety Views' psfTemplate={psfTemplate}
                        groups={groups} esdLinks={esdLinks} updateLinks={setEsdLinks} />
                </SplitPane>
            </div>
            {error ? <ErrorAlertComponent message={error} />  : null}
        </Stack>
    </Box>
    );
}

